import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Target, Plus } from "lucide-react";
import { toast } from "sonner";

const empty = { child_id: "", title: "", description: "", category: "speech", term: "short", progress: 0, target_date: "" };
const CATS = { speech: "تخاطب", motor: "حركي", social: "اجتماعي", cognitive: "معرفي", behavioral: "سلوكي" };

export default function IEPPage() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [children, setChildren] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const canCreate = user.role === "admin" || user.role === "teacher";

  const load = async () => {
    const [{ data: g }, { data: c }] = await Promise.all([
      api.get("/iep-goals"),
      api.get("/children"),
    ]);
    setGoals(g);
    setChildren(c);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.child_id || !form.title) { toast.error("املأ الحقول المطلوبة"); return; }
    try {
      await api.post("/iep-goals", form);
      toast.success("تم إضافة الهدف");
      setOpen(false);
      setForm(empty);
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const updateProgress = async (id, progress) => {
    try {
      await api.patch(`/iep-goals/${id}?progress=${progress}`);
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="iep-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900">الخطط التعليمية الفردية (IEP)</h1>
          <p className="text-slate-500 mt-2">أهداف قصيرة وطويلة المدى مع تتبع نسبة الإنجاز</p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="h-12 gap-2 bg-sky-600 hover:bg-sky-700" data-testid="new-goal-btn">
                <Plus className="w-4 h-4" /> هدف جديد
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="text-2xl font-black">هدف تعليمي جديد</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-1.5">
                  <Label className="font-bold">الطفل</Label>
                  <Select value={form.child_id} onValueChange={(v) => setForm({ ...form, child_id: v })}>
                    <SelectTrigger data-testid="goal-child"><SelectValue placeholder="اختر" /></SelectTrigger>
                    <SelectContent>{children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="font-bold">عنوان الهدف</Label>
                  <Input data-testid="goal-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-bold">الوصف</Label>
                  <Textarea data-testid="goal-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="font-bold">الفئة</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(CATS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="font-bold">المدى</Label>
                    <Select value={form.term} onValueChange={(v) => setForm({ ...form, term: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short">قصير</SelectItem>
                        <SelectItem value="long">طويل</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="font-bold">التاريخ المستهدف</Label>
                  <Input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} />
                </div>
                <Button data-testid="save-goal-btn" onClick={save} className="w-full h-12 bg-sky-600 hover:bg-sky-700">حفظ</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {goals.map((g) => (
          <Card key={g.id} data-testid={`goal-${g.id}`} className="border-slate-200/70 card-elevated">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                  <Target className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="text-xs text-slate-500">{g.child_name} · {CATS[g.category] || g.category} · {g.term === "short" ? "قصير المدى" : "طويل المدى"}</div>
                  <div className="font-black text-slate-900 text-lg mt-0.5">{g.title}</div>
                  {g.description && <div className="text-sm text-slate-600 mt-1">{g.description}</div>}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-slate-700">نسبة الإنجاز</span>
                  <span className="text-lg font-black text-emerald-700">{g.progress}%</span>
                </div>
                <Progress value={g.progress} className="h-3" />
              </div>
              {canCreate && (
                <div>
                  <Label className="text-xs font-bold text-slate-500">تحديث النسبة</Label>
                  <Slider
                    data-testid={`goal-slider-${g.id}`}
                    defaultValue={[g.progress]}
                    max={100}
                    step={5}
                    onValueCommit={(v) => updateProgress(g.id, v[0])}
                    className="mt-2"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {goals.length === 0 && <Card className="md:col-span-2"><CardContent className="p-16 text-center text-slate-400">لا توجد أهداف بعد</CardContent></Card>}
      </div>
    </div>
  );
}
